import React, { useEffect } from 'react';
import './RattachementModal.css';
import Button from '../Button/Button';

interface RattachementModalProps {
  userName?: string;
  userEmail?: string;
  onLogout: () => void;
}

const SUPPORT_EMAIL = 'contact@centralis-europe.com';

/**
 * Ecran bloquant pour un compte parque dans l'entite par defaut (aucune structure rattachee).
 *
 * Meme mecanique que SuspensionModal (FRONT-19) : inevitable (Escape neutralise, scroll bloque),
 * pilote par AuthContext.rattachementInfo, alimente par login, /me et la sonde suspension/status.
 * Le serveur refuse par ailleurs toute l'API a ces comptes (middleware EnsureEntiteRattachee) :
 * cet ecran informe et oriente vers le support, il ne protege pas a lui seul.
 */
const RattachementModal: React.FC<RattachementModalProps> = ({ userName, userEmail, onLogout }) => {
  // Empecher la touche Escape de fermer le modal et figer le scroll de la page derriere.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = '';
    };
  }, []);

  // Mail pre-rempli : le support a besoin du nom, de la structure et du SIRET pour rattacher.
  const sujet = encodeURIComponent('Rattachement de mon compte Centralis');
  const corps = encodeURIComponent(
    `Bonjour,\n\nMon compte Centralis${userEmail ? ` (${userEmail})` : ''} n'est rattaché à aucune structure.\n\n` +
    `Nom : ${userName ?? ''}\nEntreprise ou organisme : \nSIRET : \nFonction : \n\n` +
    `Merci de procéder au rattachement.\n\nCordialement`
  );
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${sujet}&body=${corps}`;

  return (
    <div className="rattachement-overlay" role="dialog" aria-modal="true" aria-labelledby="rattachement-titre">
      <div className="rattachement-modal">
        <div className="rattachement-modal-header">
          <div className="rattachement-modal-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" />
              <path d="M5 21V7l7-4 7 4v14" />
              <path d="M9 21v-6h6v6" />
              <line x1="12" y1="10" x2="12.01" y2="10" />
            </svg>
          </div>
          <h2 id="rattachement-titre">Votre compte n'est rattaché à aucune structure</h2>
        </div>

        <div className="rattachement-modal-body">
          <p className="rattachement-text-primary">
            Votre compte a bien été créé, mais il n'est pour l'instant rattaché à aucune entreprise ni organisme.
          </p>
          <p className="rattachement-text-secondary">
            Par précaution, l'accès à la plateforme est bloqué tant que notre équipe n'a pas finalisé votre
            rattachement. Écrivez-nous en précisant votre nom, l'entreprise ou l'organisme que vous
            représentez et son SIRET ; nous vous rattachons et vous prévenons dès que c'est fait.
          </p>

          <div className="rattachement-contact">
            <p>Notre support :</p>
            <a href={mailto} className="rattachement-contact-link">{SUPPORT_EMAIL}</a>
          </div>
        </div>

        <div className="rattachement-modal-footer">
          <Button variant="secondary" value="Se déconnecter" onClick={onLogout} />
          <Button type="link" linkHref={mailto} targetBlank={false} value="Contacter le support" />
        </div>
      </div>
    </div>
  );
};

export default RattachementModal;
